/**
 * Utilitários da janela de mensagens Meta (Instagram Direct + Messenger).
 *
 * Política atual (sem feature Human Agent aprovada no app Meta):
 * - 0–24h: RESPONSE livre
 * - >24h: bloqueado — aguardar nova mensagem do cliente
 *
 * A janela é do cliente no canal (não da solicitação).
 * Paridade com xpro4api/src/utils/metaMessagingWindow.js
 */

import {
  getLastClientMessageDate,
  resolveMessagingWindowLastInboundDate,
  channelTypeToMessagingWindowFamily,
} from "./whatsappWindowUtils";

const WINDOW_HOURS = 24;
const WARNING_START_HOURS = 20;

/**
 * @param {string | Date | number | null | undefined} lastDate
 * @param {{ channelLabel?: string }} [opts]
 * @returns {{
 *   status: 'ok' | 'aboutToExpire' | 'expired',
 *   hoursLeft: number | null,
 *   tooltip: string,
 * }}
 */
export function getMetaMessagingWindowState(lastDate, opts = {}) {
  const channelLabel = opts.channelLabel || "Instagram/Messenger";

  if (lastDate == null) {
    return {
      status: "ok",
      hoursLeft: null,
      tooltip: "",
    };
  }

  const date =
    typeof lastDate === "object" && lastDate instanceof Date
      ? lastDate
      : new Date(lastDate);

  if (Number.isNaN(date.getTime())) {
    return { status: "ok", hoursLeft: null, tooltip: "" };
  }

  const diffMs = Date.now() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < WARNING_START_HOURS) {
    return {
      status: "ok",
      hoursLeft: Math.max(0, WINDOW_HOURS - diffHours),
      tooltip: "",
    };
  }

  if (diffHours < WINDOW_HOURS) {
    const hoursLeft = Math.max(0, WINDOW_HOURS - diffHours);
    const rounded = Math.max(1, Math.floor(hoursLeft));
    return {
      status: "aboutToExpire",
      hoursLeft,
      tooltip: `Janela de 24h do ${channelLabel} deste cliente expira em aproximadamente ${rounded} ${
        rounded === 1 ? "hora" : "horas"
      }. Depois disso, será necessário aguardar o cliente enviar uma nova mensagem.`,
    };
  }

  return {
    status: "expired",
    hoursLeft: 0,
    tooltip: `A janela de 24 horas do ${channelLabel} deste cliente expirou. Aguarde o cliente enviar uma nova mensagem para reabrir o atendimento.`,
  };
}

/**
 * Canais Meta com janela de 24h (tipos 12 e 21, ou nomes de channelType).
 */
export function isMetaMessagingWindowChannel(channelType, channelTipo = null) {
  const tipo = channelTipo != null ? Number(channelTipo) : NaN;
  if (tipo === 12 || tipo === 21) return true;
  const key = String(channelType || "")
    .trim()
    .toLowerCase();
  return key === "messenger" || key === "instagram" || key === "facebook";
}

export function getMetaChannelLabel(channelType) {
  const key = String(channelType || "")
    .trim()
    .toLowerCase();
  if (key === "instagram" || key.includes("instagram")) return "Instagram";
  if (key === "messenger" || key === "facebook") return "Messenger";
  return "Instagram/Messenger";
}

export {
  getLastClientMessageDate,
  resolveMessagingWindowLastInboundDate,
  channelTypeToMessagingWindowFamily,
  WINDOW_HOURS,
};
